// ============================================================================
// G-001 — Photo Download & Compare Service (KESFace biometric)
// ============================================================================
// Setiap kandidat OSINT punya N photo URLs dari Stage 2 enrichment.
// Compare setiap photo vs subject_photo (KTP/Datacenter) via /api/face-compare
// (KESFace InsightFace 512-d cosine similarity).
//
// Output: { match_count, total_count, attempted, failed, best_similarity, explanation }
//
// Production constraints (per OSINT Flow Doc + dev experience):
//   - Max 20 foto per kandidat (cap, Feature 003 § 4.4a)
//   - Threshold 0.85 cosine = match
//   - IG/FB photo URLs bisa expired post-Actor-run (CDN signed-URL TTL pendek)
//     → graceful fallback: hitung dari URL yang berhasil saja
//   - 401/403 = expired/auth → log + skip
//   - 5xx = HF Space hiccup → log + skip (hindari false negative)
//   - Timeout 15s per foto (parallel via Promise.allSettled)
//   - De-dupe URL sebelum compare (kadang Apify return duplicate)
// ============================================================================

import { getAuthHeaders } from '../auth';
import { OSINT_THRESHOLDS } from './thresholds';

// Threshold realistis cross-domain (KTP vs sosmed): 0.60 = match valid.
// Sebelumnya 0.85 terlalu strict — KTP & sosmed beda lighting/angle/age signifikan.
// Threshold dari ./thresholds.js single source (mirror api/_thresholds.js).
const FACE_COMPARE_THRESHOLD = OSINT_THRESHOLDS.photoCompare.similarity;
const PHOTO_TIMEOUT_MS       = 10000;  // 15s → 10s untuk Hobby 60s budget
const MAX_PHOTOS_DEFAULT     = 10;     // Hobby compat: 20 → 10 (timeout safety)
const MIN_VALID_FOR_SCORE    = 1;
const HF_WARMUP_TIMEOUT_MS   = 8000;

// In-memory cache hasil compare per (subjectUrl + candidateUrl) dalam 1 session
// Hindari re-compare URL yang sama (mis. user re-run OSINT pada subject sama)
const _compareCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 menit

function cacheKey(subjectUrl, candidateUrl) {
  return `${subjectUrl}::${candidateUrl}`;
}

function getCached(subjectUrl, candidateUrl) {
  const k = cacheKey(subjectUrl, candidateUrl);
  const entry = _compareCache.get(k);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    _compareCache.delete(k);
    return null;
  }
  return entry.value;
}

function setCached(subjectUrl, candidateUrl, value) {
  _compareCache.set(cacheKey(subjectUrl, candidateUrl), { ts: Date.now(), value });
  // Keep cache size bounded
  if (_compareCache.size > 500) {
    const firstKey = _compareCache.keys().next().value;
    _compareCache.delete(firstKey);
  }
}

/**
 * Compare semua photo URLs kandidat vs subject photo (Datacenter KTP).
 *
 * @param {string[]} photoUrls         - Array URL foto dari kandidat
 * @param {string}   subjectPhotoUrl   - Subject photo dari Datacenter
 * @param {number}   maxPhotos         - Cap jumlah foto yang di-compare (default 20)
 * @returns {Promise<object|null>}     - { match_count, total_count, attempted, failed, best_similarity, explanation, threshold }
 */
export async function comparePhotosWithSubject(photoUrls, subjectOrUrl, maxPhotos = MAX_PHOTOS_DEFAULT) {
  // Accept either subject object {foto_url, face_embedding} OR raw URL string
  const subjectPhotoUrl  = typeof subjectOrUrl === 'string'
    ? subjectOrUrl
    : subjectOrUrl?.foto_url || null;
  const subjectEmbedding = typeof subjectOrUrl === 'object' && Array.isArray(subjectOrUrl?.face_embedding)
    ? subjectOrUrl.face_embedding
    : null;

  // Defensive: butuh either embedding (preferred) atau photo URL fallback
  if (!subjectPhotoUrl && !subjectEmbedding) {
    return {
      match_count: 0, total_count: 0, attempted: 0, failed: 0,
      best_similarity: 0, threshold: FACE_COMPARE_THRESHOLD,
      explanation: 'Subject biometric unavailable for comparison',
    };
  }
  if (!Array.isArray(photoUrls) || photoUrls.length === 0) {
    return {
      match_count: 0, total_count: 0, attempted: 0, failed: 0,
      best_similarity: 0, threshold: FACE_COMPARE_THRESHOLD,
      explanation: 'No candidate photos to compare',
    };
  }

  // De-dupe + filter valid URL strings + slice cap
  const urls = [...new Set(
    photoUrls.filter(u => typeof u === 'string' && u.startsWith('http'))
  )].slice(0, maxPhotos);

  if (urls.length === 0) {
    return {
      match_count: 0, total_count: 0, attempted: 0, failed: 0,
      best_similarity: 0, threshold: FACE_COMPARE_THRESHOLD,
      explanation: 'No valid candidate photo URLs after filtering',
    };
  }

  // Per-photo compare dengan in-memory cache (per session, 5min TTL).
  // Hindari re-compare URL yang sama saat operator re-run OSINT.
  // Per-foto request supaya skip 401/403/timeout tanpa kegagalan total.
  // Concurrency limit 4 → tidak overload HF Space saat banyak photos
  // (HF Space cuma 1 vCPU di T4 Small).
  const CONCURRENCY = 4;
  const queue = [...urls];
  const results = [];

  async function worker() {
    while (queue.length > 0) {
      const url = queue.shift();
      if (!url) continue;
      // Check cache first — key by subject identity (embedding hash atau URL)
      const cacheRefKey = subjectEmbedding ? `emb:${subjectEmbedding.length}:${(subjectEmbedding[0] || 0).toFixed(4)}` : subjectPhotoUrl;
      const cached = getCached(cacheRefKey, url);
      if (cached) {
        results.push({ status: 'fulfilled', value: cached });
        continue;
      }
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), PHOTO_TIMEOUT_MS);
      try {
        // Pakai pre-computed subject embedding kalau ada (skip subject re-extract)
        const reqBody = subjectEmbedding
          ? { subject_embedding: subjectEmbedding, candidate_photos: [url] }
          : { subject_photo:     subjectPhotoUrl,  candidate_photos: [url] };
        const res = await fetch('/api/face-compare', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body:    JSON.stringify(reqBody),
          signal: ctrl.signal,
        });
        clearTimeout(timer);
        if (!res.ok) {
          if (res.status === 401 || res.status === 403 || res.status === 410) {
            console.warn(`[G-001] photo expired (HTTP ${res.status}): ${url.slice(0, 80)}`);
          } else {
            console.warn(`[G-001] face-compare HTTP ${res.status}: ${url.slice(0, 80)}`);
          }
          const value = { ok: false, status: res.status };
          setCached(cacheRefKey, url, value); // cache failures to skip retry
          results.push({ status: 'fulfilled', value });
          continue;
        }
        const json = await res.json();
        const value = { ok: true, similarity: json.best_similarity || 0 };
        setCached(cacheRefKey, url, value);
        results.push({ status: 'fulfilled', value });
      } catch (err) {
        clearTimeout(timer);
        const reason = err.name === 'AbortError' ? 'timeout' : err.message;
        console.warn(`[G-001] compare fail (${reason}): ${url.slice(0, 80)}`);
        results.push({ status: 'fulfilled', value: { ok: false, error: reason } });
      }
    }
  }

  // Run CONCURRENCY workers parallel
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, urls.length) }, () => worker()));

  // Aggregate
  const valid    = results.filter(r => r.status === 'fulfilled' && r.value.ok);
  const failed   = results.length - valid.length;
  const matches  = valid.filter(v => v.value.similarity >= FACE_COMPARE_THRESHOLD).length;
  const bestSim  = valid.length === 0 ? 0 : Math.max(...valid.map(v => v.value.similarity));

  let explanation;
  if (valid.length < MIN_VALID_FOR_SCORE) {
    explanation = `Photo comparison unavailable — ${urls.length} URLs gagal akses (kemungkinan expired post-Actor-run)`;
  } else {
    explanation = `${matches} of ${valid.length} candidate photos match Datacenter photo (similarity ≥ ${FACE_COMPARE_THRESHOLD})`;
    if (failed > 0) explanation += ` · ${failed} photo URL skipped (expired/error)`;
  }

  return {
    match_count:     matches,
    total_count:     valid.length,
    attempted:       urls.length,
    failed:          failed,
    best_similarity: Number(bestSim.toFixed(3)),
    threshold:       FACE_COMPARE_THRESHOLD,
    explanation,
  };
}

/**
 * Compute photo match score for confidence scoring (0..1 normalized).
 * Slight boost untuk partial match — jangan zero kalau ada SOME match.
 */
export function computePhotoMatchScore(compareResult) {
  if (!compareResult || compareResult.total_count < MIN_VALID_FOR_SCORE) return 0;
  const ratio = compareResult.match_count / compareResult.total_count;
  // Slight boost (1.2×) untuk partial match — clipped to 1.0
  return Math.min(1, ratio * 1.2);
}

/**
 * Format Stage 4 Joint Confidence phrasing per PRD § 4.4a.
 * Bilingual ID/EN. Single-photo confidence INSUFFICIENT — selalu pakai X / Y.
 *
 * @param {object} result  Output dari comparePhotosWithSubject
 * @param {string} lang    'id' | 'en'
 * @returns {string}       Phrasing operator-readable, atau '' jika data tidak cukup
 */
export function formatPhotoMatchExplanation(result, lang = 'id') {
  if (!result) return '';
  const { match_count, total_count, threshold } = result;
  if (typeof total_count !== 'number' || total_count < 1) {
    return lang === 'id'
      ? 'Foto kandidat tidak dapat diverifikasi (URL kadaluwarsa atau tidak dapat diakses)'
      : 'Candidate photos could not be verified (URLs expired or inaccessible)';
  }
  const thr = (threshold ?? FACE_COMPARE_THRESHOLD).toFixed(2);
  if (lang === 'id') {
    return `${match_count} dari ${total_count} foto kandidat cocok dengan galeri Datacenter (similarity ≥ ${thr})`;
  }
  return `${match_count} of ${total_count} candidate photos match Datacenter gallery (similarity ≥ ${thr})`;
}

/**
 * Klasifikasi face verification band per PRD § 4.4a thresholds.
 * Dipakai untuk warna badge di UI.
 *
 * @returns {'high' | 'medium' | 'low' | 'unverified'}
 */
export function classifyFaceVerification(result) {
  if (!result || result.total_count < MIN_VALID_FOR_SCORE) return 'unverified';
  const { match_count, total_count } = result;
  if (match_count >= 3 && total_count >= 4) return 'high';      // ≥3 matches dari ≥4 fotos
  if (match_count >= 2)                      return 'medium';   // 2 matches
  if (match_count >= 1)                      return 'low';      // 1 match
  return 'unverified';
}
